import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, ArrowRight, CheckCircle, AlertCircle, HelpCircle, X, Shield, KeyRound, ArrowLeft, Link2 } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';

import { CDN_URL } from '@/config/cdn';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const CustomerLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect');
  const isResourceLink = redirectPath?.startsWith('/r/');
  const shortCode = isResourceLink ? redirectPath.replace('/r/', '') : null;

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showHelp, setShowHelp] = useState(false);
  const [resourceMeta, setResourceMeta] = useState(null);

  // Fetch resource link metadata for preview when coming from a resource link
  useEffect(() => {
    if (!isResourceLink || !shortCode) return;

    // Store redirect path in sessionStorage so MagicLinkVerify can use it
    sessionStorage.setItem('loginRedirect', redirectPath);

    const fetchMeta = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/public/resource-links/${shortCode}`);
        if (response.ok) {
          const data = await response.json();
          setResourceMeta(data);
        }
      } catch {
        // Silently fail - preview is optional
      }
    };
    fetchMeta();
  }, [isResourceLink, shortCode, redirectPath]);

  // Login method: 'magic-link', 'otp', or 'password'
  const [loginMethod, setLoginMethod] = useState('magic-link');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Magic link flow
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  // OTP flow
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [otpEmail, setOtpEmail] = useState(''); // Store email for OTP verification
  const otpInputRefs = useRef([]);

  // Handle magic link request
  const handleMagicLinkSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Store redirect path for MagicLinkVerify to pick up
    if (redirectPath) {
      sessionStorage.setItem('loginRedirect', redirectPath);
    }

    try {
      const response = await fetch(`${API_BASE}/api/member/request-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          origin: window.location.origin
        })
      });

      if (response.status === 429) {
        setError('Too many sign-in requests. Please try again later.');
        return;
      }

      setMagicLinkSent(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP request
  const handleOtpRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Store redirect path for after verification
    if (redirectPath) {
      sessionStorage.setItem('loginRedirect', redirectPath);
    }

    try {
      const response = await fetch(`${API_BASE}/api/member/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.toLowerCase().trim()
        })
      });

      if (response.status === 429) {
        setError('Too many code requests. Please try again later.');
        return;
      }

      setOtpEmail(email.toLowerCase().trim());
      setOtpSent(true);
      setOtpCode(['', '', '', '', '', '']);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input change
  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otpCode];
    newOtp[index] = value;
    setOtpCode(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  // Handle OTP paste
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\s/g, '');
    if (/^\d{6}$/.test(pastedData)) {
      setOtpCode(pastedData.split(''));
      otpInputRefs.current[5]?.focus();
    }
  };

  // Handle OTP backspace
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  // Handle OTP verification
  const handleOtpVerify = async () => {
    const code = otpCode.join('');
    if (code.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/member/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: otpEmail,
          code: code
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.detail || 'Invalid code. Please try again.');
        setOtpCode(['', '', '', '', '', '']);
        otpInputRefs.current[0]?.focus();
        return;
      }

      // Store auth data (same as magic link verification)
      if (data.auth_type === 'student') {
        localStorage.setItem('memberToken', data.token);
        localStorage.setItem('memberData', JSON.stringify({
          auth_type: data.auth_type,
          entity_id: data.entity_id,
          entity: data.entity,
          permissions: data.permissions
        }));
      } else {
        localStorage.setItem('memberToken', data.token);
        localStorage.setItem('customerToken', data.token);
        localStorage.setItem('customerData', JSON.stringify(data.customer));
        localStorage.setItem('memberData', JSON.stringify({
          auth_type: 'customer',
          entity_id: data.customer.id,
          entity: data.customer,
          permissions: { can_view_billing: true, can_view_siblings: true }
        }));
      }

      // Redirect to dashboard or stored redirect path
      const storedRedirect = sessionStorage.getItem('loginRedirect');
      sessionStorage.removeItem('loginRedirect');
      navigate(storedRedirect || '/');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE}/api/student/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: username.trim(),
          password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.detail || 'Invalid username or password.');
        return;
      }

      localStorage.setItem('memberToken', data.token);
      localStorage.setItem('memberData', JSON.stringify({
        auth_type: data.auth_type,
        entity_id: data.entity_id,
        entity: data.entity,
        permissions: data.permissions
      }));

      const storedRedirect = sessionStorage.getItem('loginRedirect');
      sessionStorage.removeItem('loginRedirect');
      navigate(storedRedirect || '/');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit when all OTP digits are entered
  useEffect(() => {
    if (otpCode.every(d => d !== '') && otpCode.length === 6) {
      handleOtpVerify();
    }
  }, [otpCode]);

  // Magic link sent confirmation
  if (magicLinkSent) {
    return (
      <div className="member-login-bg relative flex items-center justify-center px-4">
        <div className="login-orb login-orb-1"></div>
        <div className="login-orb login-orb-2"></div>
        <div className="login-orb login-orb-3"></div>

        <Card className="member-login-card w-full max-w-md relative z-10 dojo-animate">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-[#C9A227] to-[#9B1B30] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#9B1B30]/30">
              <CheckCircle className="h-8 w-8 text-white" />
            </div>
            <h2 className="text-2xl font-outfit font-bold text-stone-800 dark:text-stone-100 mb-2">Check Your Email</h2>
            <p className="text-stone-600 dark:text-stone-400 mb-4 font-dm-sans">
              If your email is authorized, you'll receive a sign-in link shortly.
            </p>
            <p className="text-sm text-stone-500 dark:text-stone-500 font-dm-sans">
              The link will expire in <span className="font-jetbrains font-bold text-[#9B1B30]">15</span> minutes.
            </p>
            <Button
              variant="memberSecondary"
              className="mt-6"
              onClick={() => { setMagicLinkSent(false); setEmail(''); }}
            >
              Try a different email
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // OTP code entry screen
  if (otpSent) {
    return (
      <div className="member-login-bg relative flex items-center justify-center px-4">
        <div className="login-orb login-orb-1"></div>
        <div className="login-orb login-orb-2"></div>
        <div className="login-orb login-orb-3"></div>

        <Card className="member-login-card w-full max-w-md relative z-10 dojo-animate">
          <CardHeader className="text-center pb-2">
            <button
              onClick={() => { setOtpSent(false); setOtpCode(['', '', '', '', '', '']); setError(''); }}
              className="absolute left-4 top-4 text-stone-500 dark:text-stone-400 hover:text-[#9B1B30] transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="w-16 h-16 bg-gradient-to-br from-[#C9A227] to-[#9B1B30] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#9B1B30]/30">
              <KeyRound className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-outfit font-bold text-stone-800 dark:text-stone-100">Enter Your Code</CardTitle>
            <CardDescription className="text-stone-600 dark:text-stone-400 font-dm-sans mt-2">
              We sent a 6-digit code to<br />
              <span className="font-medium text-stone-800 dark:text-stone-200">{otpEmail}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {/* OTP Input Grid */}
            <div className="flex justify-center gap-2 mb-6" onPaste={handleOtpPaste}>
              {otpCode.map((digit, index) => (
                <input
                  key={index}
                  ref={el => otpInputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className="w-12 h-14 text-center text-2xl font-jetbrains font-bold border-2 border-stone-300 dark:border-stone-600 rounded-lg
                             focus:border-[#9B1B30] focus:ring-2 focus:ring-[#9B1B30]/20 focus:outline-none
                             bg-white dark:bg-stone-800 text-stone-800 dark:text-stone-100 transition-all"
                  disabled={loading}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg font-dm-sans mb-4">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              onClick={handleOtpVerify}
              variant="memberPrimary"
              className="w-full h-12 text-base"
              disabled={loading || otpCode.some(d => d === '')}
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </Button>

            <p className="text-sm text-stone-500 dark:text-stone-400 font-dm-sans text-center mt-4">
              Code expires in <span className="font-jetbrains font-bold text-[#9B1B30]">10</span> minutes
            </p>

            <div className="mt-4 text-center">
              <button
                onClick={() => { handleOtpRequest({ preventDefault: () => {} }); }}
                disabled={loading}
                className="text-sm text-[#9B1B30] hover:text-[#6B1220] hover:underline font-medium transition-colors font-dm-sans"
              >
                Didn't receive it? Send new code
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main login form
  return (
    <div className="member-login-bg relative flex items-center justify-center px-4">
      <div className="login-orb login-orb-1"></div>
      <div className="login-orb login-orb-2"></div>
      <div className="login-orb login-orb-3"></div>

      <Card className="member-login-card w-full max-w-md relative z-10 dojo-animate">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-[#C9A227]/40 blur-lg animate-pulse scale-110"></div>
              <img
                src={`${CDN_URL}/images/owaza-logo.jpg`}
                alt="Owaza Martial Arts"
                className="relative h-24 w-24 object-cover rounded-full shadow-2xl ring-4 ring-[#C9A227]/40"
              />
            </div>
          </div>
          <CardTitle className="text-3xl font-outfit font-bold text-stone-800 dark:text-stone-100">Member Sign In</CardTitle>
          <CardDescription className="flex items-center justify-center gap-1 text-stone-600 dark:text-stone-400 font-dm-sans mt-2">
            {loginMethod === 'magic-link'
              ? 'Enter your email to receive a sign-in link'
              : loginMethod === 'otp'
                ? 'Enter your email to receive a sign-in code'
                : 'Enter your student username and password'}
            <button
              type="button"
              onClick={() => setShowHelp(true)}
              className="text-stone-400 dark:text-stone-500 hover:text-[#9B1B30] transition-colors"
              aria-label="Learn how this works"
            >
              <HelpCircle className="h-4 w-4" />
            </button>
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Resource Link Preview */}
          {isResourceLink && resourceMeta && (
            <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-start gap-2">
                <Link2 className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-stone-800 dark:text-stone-200 font-dm-sans">
                    {resourceMeta.title}
                  </p>
                  {resourceMeta.description && (
                    <p className="text-xs text-stone-500 dark:text-stone-400 mt-1 font-dm-sans">
                      {resourceMeta.description}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {isResourceLink && !resourceMeta && (
            <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-center gap-2">
                <Link2 className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <p className="text-sm text-stone-600 dark:text-stone-400 font-dm-sans">
                  Sign in to access this shared resource
                </p>
              </div>
            </div>
          )}

          {/* Method Toggle */}
          <div className="grid grid-cols-3 gap-2 mb-4 p-1 bg-stone-100 dark:bg-stone-700 rounded-lg">
            <button
              type="button"
              onClick={() => setLoginMethod('magic-link')}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all font-dm-sans ${
                loginMethod === 'magic-link'
                  ? 'bg-white dark:bg-stone-600 text-stone-800 dark:text-stone-100 shadow-sm'
                  : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'
              }`}
            >
              <Mail className="h-4 w-4 inline mr-1.5 -mt-0.5" />
              Magic Link
            </button>
            <button
              type="button"
              onClick={() => setLoginMethod('otp')}
              className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all font-dm-sans ${
                loginMethod === 'otp'
                  ? 'bg-white dark:bg-stone-600 text-stone-800 dark:text-stone-100 shadow-sm'
                  : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'
              }`}
            >
              <KeyRound className="h-4 w-4 inline mr-1.5 -mt-0.5" />
              Code
            </button>
            <button
              type="button"
              onClick={() => setLoginMethod('password')}
              className={`py-2 px-3 rounded-md text-sm font-medium transition-all font-dm-sans ${
                loginMethod === 'password'
                  ? 'bg-white dark:bg-stone-600 text-stone-800 dark:text-stone-100 shadow-sm'
                  : 'text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200'
              }`}
            >
              <KeyRound className="h-4 w-4 inline mr-1.5 -mt-0.5" />
              Password
            </button>
          </div>

          {loginMethod === 'password' ? (
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <div className="relative">
                <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#9B1B30]" />
                <Input
                  type="text"
                  placeholder="student-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoComplete="username"
                  className="pl-10 h-12 bg-white dark:bg-stone-800 border-stone-300 dark:border-stone-600 focus:border-[#9B1B30] focus:ring-[#9B1B30]/20 font-dm-sans text-stone-800 dark:text-stone-100"
                  disabled={loading}
                />
              </div>
              <div className="relative">
                <KeyRound className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#9B1B30]" />
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="pl-10 h-12 bg-white dark:bg-stone-800 border-stone-300 dark:border-stone-600 focus:border-[#9B1B30] focus:ring-[#9B1B30]/20 font-dm-sans text-stone-800 dark:text-stone-100"
                  disabled={loading}
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg font-dm-sans">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <Button
                type="submit"
                variant="memberPrimary"
                className="w-full h-12 text-base"
                disabled={loading || !username || !password}
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          ) : (
            <form onSubmit={loginMethod === 'magic-link' ? handleMagicLinkSubmit : handleOtpRequest} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#9B1B30]" />
              <Input
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="pl-10 h-12 bg-white dark:bg-stone-800 border-stone-300 dark:border-stone-600 focus:border-[#9B1B30] focus:ring-[#9B1B30]/20 font-dm-sans text-stone-800 dark:text-stone-100"
                disabled={loading}
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg font-dm-sans">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              variant="memberPrimary"
              className="w-full h-12 text-base"
              disabled={loading || !email}
            >
              {loading ? (
                'Sending...'
              ) : (
                <>
                  {loginMethod === 'magic-link' ? 'Send Sign-In Link' : 'Send Sign-In Code'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </form>
          )}

          {loginMethod === 'otp' && (
            <p className="text-xs text-center text-stone-500 dark:text-stone-400 mt-3 font-dm-sans">
              Use this option for home screen apps where links don't work
            </p>
          )}

          <div className="mt-6 text-center text-sm text-stone-600 dark:text-stone-400 font-dm-sans">
            {isResourceLink ? (
              <p>
                New to Owaza?{' '}
                <a
                  href={`https://owazamartialarts.com/register?mode=new&ref=${shortCode}`}
                  className="text-[#9B1B30] hover:text-[#6B1220] hover:underline font-medium transition-colors"
                >
                  Register here
                </a>
              </p>
            ) : (
              <p>
                Only authorized members can sign in.{' '}
                <a href="/" className="text-[#9B1B30] hover:text-[#6B1220] hover:underline font-medium transition-colors">
                  Contact us
                </a>{' '}
                to get started.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Help Modal */}
      {showHelp && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="member-login-card max-w-sm w-full dojo-animate">
            <div className="flex items-center justify-between p-4 border-b border-stone-200 dark:border-stone-700">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-[#C9A227]" />
                <h3 className="font-outfit font-bold text-stone-800 dark:text-stone-100">Passwordless Sign-In</h3>
              </div>
              <button
                onClick={() => setShowHelp(false)}
                className="text-stone-500 dark:text-stone-400 hover:text-[#9B1B30] transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-3 text-sm text-stone-600 dark:text-stone-400 font-dm-sans">
              <p>
                <strong className="text-stone-800 dark:text-stone-200">Magic Link:</strong> We send a secure link to your email.
                Click it to sign in instantly—no password needed.
              </p>
              <p>
                <strong className="text-stone-800 dark:text-stone-200">Sign-In Code:</strong> We send a 6-digit code to your email.
                Enter it here to sign in. Best for home screen apps.
              </p>
              <p>
                <strong className="text-stone-800 dark:text-stone-200">Why it's secure:</strong>
              </p>
              <ul className="list-disc list-inside space-y-1 text-stone-600 dark:text-stone-400">
                <li>Links expire in <span className="font-jetbrains font-bold text-[#9B1B30]">15</span> minutes, codes in <span className="font-jetbrains font-bold text-[#9B1B30]">10</span></li>
                <li>Each link/code works only once</li>
                <li>No passwords to steal or forget</li>
                <li>Only you can access your email</li>
              </ul>
            </div>
            <div className="p-4 border-t border-stone-200 dark:border-stone-700">
              <Button
                onClick={() => setShowHelp(false)}
                variant="memberPrimary"
                className="w-full"
                size="sm"
              >
                Got it
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerLogin;
